import React, { useEffect, useRef } from 'react';
import { Anchor, Compass, Gauge, Ship } from 'lucide-react';
import { cn } from '@/utils';
import useAuthStore from '@/store/auth';
import { Vessel } from '@/types/api';
import { timeAgo } from '@/utils/datetime';
import Stat from './Stat';

const statusTone = {
  online: {
    label: 'Online',
    cls: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-900/50',
    dot: 'bg-green-500',
  },
  offline: {
    label: 'Offline',
    cls: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50',
    dot: 'bg-red-500',
  },
};

const shipLabel: Record<string, string> = {
  tugboat: 'Tugboat',
  tanker: 'Tanker',
  container: 'Container',
  bulk: 'Bulk',
  ferry: 'Ferry',
  cargo: 'Cargo',
  fishing: 'Fishing',
  yacht: 'Yacht',
  barge: 'Barge',
  passenger: 'Passenger',
  patrol: 'Patrol',
  research: 'Research',
};

const normalizeShipType = (type?: string): string => {
  if (!type) return 'cargo';
  const t = type.toLowerCase().replace(/[\s-_]+/g, '');
  if (t.includes('tug')) return 'tugboat';
  if (t.includes('tanker')) return 'tanker';
  if (t.includes('container')) return 'container';
  if (t.includes('bulk')) return 'bulk';
  if (t.includes('ferry')) return 'ferry';
  if (t.includes('cargo')) return 'cargo';
  if (t.includes('fishing')) return 'fishing';
  if (t.includes('yacht')) return 'yacht';
  if (t.includes('barge')) return 'barge';
  if (t.includes('passenger')) return 'passenger';
  if (t.includes('patrol')) return 'patrol';
  if (t.includes('research')) return 'research';
  return 'cargo';
};

const isVesselOffline = (v: Vessel): boolean => {
  if (v.is_off) return true;
  const lat = parseFloat(v.latitude);
  const lng = parseFloat(v.longitude);
  return isNaN(lat) || isNaN(lng) || !v.latest_receive_date;
};

interface VesselMapCardProps {
  vessel: Vessel;
}

export default function VesselMapCard({ vessel }: VesselMapCardProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const user = useAuthStore((state) => state.user);
  const theme = user?.settingsObj?.theme;

  const isOffline = isVesselOffline(vessel);
  const tone = isOffline ? statusTone.offline : statusTone.online;
  const lat = parseFloat(vessel.latitude);
  const lon = parseFloat(vessel.longitude);
  const heading = parseFloat(vessel.heading) || 0;
  const isCoordsInvalid = isNaN(lat) || isNaN(lon);
  const normalizedType = normalizeShipType(vessel.ship_type);
  const typeLabel = shipLabel[normalizedType] || 'Vessel';

  useEffect(() => {
    if (isCoordsInvalid || !mapRef.current) return;
    let cancelled = false;
    let map: import('leaflet').Map | null = null;

    (async () => {
      const L = (await import('leaflet')).default;
      if (cancelled || !mapRef.current) return;

      map = L.map(mapRef.current, {
        center: [lat, lon],
        zoom: 12,
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: true,
        dragging: true,
      });

      const tileUrl = 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}';

      L.tileLayer(tileUrl, {
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        maxZoom: 20,
        attribution: '&copy; Google Maps',
      }).addTo(map);

      const icon = L.divIcon({
        className: '',
        html: `
          <div style="position:relative;width:34px;height:34px;display:flex;align-items:center;justify-content:center;">
            <div style="position:absolute;inset:0;border-radius:9999px;background:rgba(0, 150, 251, 0.18);"></div>
            <div style="position:absolute;inset:6px;border-radius:9999px;background:rgba(0, 150, 251, 0.32);"></div>
            <div style="position:relative;width:14px;height:14px;border-radius:9999px;background:#0096FB;box-shadow:0 0 0 2px white;transform:rotate(${heading}deg);">
              <div style="position:absolute;top:-6px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:7px solid #0096FB;"></div>
            </div>
          </div>
        `,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });

      L.marker([lat, lon], { icon }).addTo(map);

      const resizeObserver = new ResizeObserver(() => {
        if (map) {
          map.invalidateSize();
        }
      });
      resizeObserver.observe(mapRef.current);

      (map as any)._resizeObserver = resizeObserver;
    })();

    return () => {
      cancelled = true;
      if (map) {
        if ((map as any)._resizeObserver) {
          (map as any)._resizeObserver.disconnect();
        }
        map.remove();
      }
    };
  }, [lat, lon, heading, isCoordsInvalid, theme]);

  return (
    <article className="group rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden hover:shadow-md hover:border-brand-500/40 dark:hover:border-brand-500/30 transition-all">
      <div className="relative">
        {isCoordsInvalid ? (
          <div className="h-56 w-full bg-gray-50 dark:bg-gray-800/50 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
            <Compass className="h-8 w-8 mb-2 animate-pulse text-gray-300 dark:text-gray-600" />
            <span className="text-xs font-medium">No Coordinates Available</span>
          </div>
        ) : (
          <div ref={mapRef} className="h-56 w-full bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800" />
        )}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/90 dark:bg-gray-900/90 backdrop-blur text-[11px] font-medium border border-gray-200 dark:border-gray-800 shadow-sm text-gray-700 dark:text-gray-300">
          <Ship className="h-3 w-3 text-brand-500" />
          {typeLabel}
        </div>
        <div
          className={cn(
            'absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md backdrop-blur text-[11px] font-medium border shadow-sm',
            tone.cls,
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} />
          {tone.label}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold truncate text-gray-900 dark:text-white">
            {vessel.vessel_name}
          </h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono truncate">{vessel.id}</p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Stat icon={Anchor} label="Lat" value={isCoordsInvalid ? '-' : lat.toFixed(3)} />
          <Stat icon={Anchor} label="Lon" value={isCoordsInvalid ? '-' : lon.toFixed(3)} />
          <Stat icon={Gauge} label="Speed" value={vessel.speed ? `${vessel.speed} kn` : '-'} />
        </div>

        <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
          <span className="flex items-center gap-1">
            <Compass className="h-3 w-3 text-gray-400 dark:text-gray-500" /> {heading.toFixed(0)}°
          </span>
          <span className="tabular-nums">
            {vessel.latest_receive_date ? timeAgo(vessel.latest_receive_date) : '-'}
          </span>
        </div>
      </div>
    </article>
  );
}
